// packages/api/src/hooks/admin/usePricingTables.ts
// Phase 02.3 P06 — Admin Pricing Tables hooks (TanStack Query).

import { useEffect } from 'react';

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type { ApiSuccess, ChangeSetActiveEnrichment } from '@alamo/types';

import { apiClient } from '../../client';
import { pricingChangeSetKeys } from '../../query-keys';

// ─── Types (inline; promote to @alamo/types when shared) ──────────────────

export interface PricingBaseTable {
  id: string;
  code: string;
  name: string;
  categoryId: string;
  validFrom: string;
  validTo: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * ESCR-205 — workflow SD-1. Backend retorna esses campos quando consumido
   * via `/admin/pricing-tables/base-tables` (backfill `published`+v1 fez parte
   * da migration). Marcados opcionais aqui pra retro-compat com endpoints
   * legados / outros consumers que ainda não expõem o campo.
   */
  status?: 'draft' | 'approval_pending' | 'published' | 'rejected' | 'archived';
  version?: number;
  /**
   * ESCR-205.13 (auto-fork) — UUID da table parent. Quando admin edita uma
   * entry de uma published com flag `ENABLE_PRICING_AUTO_FORK=true`, BE
   * clona header + entries e o novo table draft aponta pra published v(N)
   * via `parent_version_id`. NULL nas tables originais (criadas do zero).
   */
  parentVersionId?: string | null;
  submittedBy?: string | null;
  submittedAt?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  publishedBy?: string | null;
  publishedAt?: string | null;
  rejectedBy?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  archivedBy?: string | null;
  archivedAt?: string | null;
}

export interface PricingBaseEntry {
  id: string;
  categoryId: string;
  // Phase 08.1 (2026-05-25): `pricing_base_tables` e `fipe_ranges` DROPADAS.
  // `fipeMin`/`fipeMax` agora vivem inline em `pricing_base_entries` como
  // colunas DECIMAL(12,2) NOT NULL. `tableId` e `fipeRangeId` preservados
  // como opcionais pra retro-compat com responses legacy (endpoint
  // `GET /admin/pricing-tables` retorna 1 row por categoria com tableId
  // sintético até o FE migrar de vez).
  tableId?: string;
  fipeRangeId?: string;
  fm: string;
  baseRateio: string;
  fipeMin: string;
  fipeMax: string;
  createdAt: string;
  updatedAt: string;
  /** Label legível (ex: "0 – 50.000"). Backend pode popular via formatador. */
  fipeLabel?: string | null;
  /** Phase 08 Plan 06 — CS ativo vinculado a esta entry (match via entryId). */
  activeChangeSet?: ChangeSetActiveEnrichment | null;
}

/**
 * Modelo de exibição de produtos (contrato ESCR-product-display-metadata).
 * Subitem informativo de detalhamento — sem preço, sem seleção, invisível ao
 * motor de precificação. `internalNotes` é só-ADM (nunca vai a cliente/PDF).
 */
export interface ProductSubitem {
  /** Rótulo exibido (obrigatório). */
  name: string;
  /** Detalhamento público (visível ao cliente). */
  description?: string | null;
  /** Observação só-ADM — nunca exposta em cotação/proposta/PDF/quote público. */
  internalNotes?: string | null;
  /** Ordem sob o produto pai — derivada da posição no array. */
  order?: number | null;
}

export interface ProductPresentation {
  name?: string | null;
  displayName?: string | null;
  description?: string | null;
  displayGroup?: string | null;
  subitems?: ProductSubitem[] | null;
}

export type ProductPresentationByCategory = Record<string, ProductPresentation>;

/**
 * Resumo de um plano vinculado a um produto ou variante (popover "EM PLANOS").
 * `planCategory` é o NOME humano da categoria (vehicle_categories.name), nunca o
 * UUID. Contrato: `docs/contracts/ESCR-product-listing-linked-plans.md`.
 * Backend já devolve ordenado por `level ASC, planName ASC`.
 */
export type LinkedPlanSummary = {
  planId: string;
  planName: string;
  planCategory: string;
  level: number;
};

export interface PricingProduct {
  id: string;
  code: string;
  name: string;
  description: string | null;
  presentationByCategory?: ProductPresentationByCategory | null;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** Modelo de exibição — grupo de exibição livre (≤80). `null` = seção default
   *  sem cabeçalho na proposta/PDF. Opcional pra compat com responses legadas. */
  displayGroup?: string | null;
  /** Modelo de exibição — subitens informativos (só exibição; invisível ao
   *  motor). Opcional pra compat com responses legadas. */
  subitems?: ProductSubitem[] | null;
  /** Modelo de exibição — observação só-ADM do produto. NUNCA vai a
   *  cliente/consultor/PDF/quote público. Opcional pra compat com legadas. */
  internalNotes?: string | null;
  /** D-24 — tipologia funcional (coverage|service|benefit|fee). Opcional pra
   *  compat com responses legadas que ainda não retornam o campo. */
  functionalType?: 'coverage' | 'service' | 'benefit' | 'fee';
  /** D-24 — tipologia comercial (priceable|mandatory_free|included|informational).
   *  Opcional pra compat com responses legadas que ainda não retornam o campo. */
  commercialType?: 'priceable' | 'mandatory_free' | 'included' | 'informational';
  /** ESCR-180.1 — rótulo pt-BR do tipo (espelho de `functionalType`). Coluna
   *  nullable: `null` = produto ainda não classificado. */
  type?: 'cobertura' | 'servico' | 'beneficio' | 'taxa' | null;
  /** ESCR-201.2 — variantes ativas do produto (count). Opcional pra compat com
   *  endpoints legados que ainda não populam o campo. */
  variantsCount?: number;
  /** ESCR-201.7 — count de planos distintos que incluem este produto.
   *  Backend popula via subquery COUNT(DISTINCT planId) em plan_products.
   *  Opcional enquanto o backend não expor — UI mostra "—" como fallback.
   *
   *  ESCR-211.17 (Cluster J Gap #25 UAT 2026-05-14) — filtra pl.status IN
   *  ('published','approval_pending') + pp.is_active=true. Draft/archived
   *  NÃO contam. */
  plansCount?: number;
  /** Cluster J Gap #25 (ESCR-211.17) — até 3 levels DISTINCT (ASC) dos planos
   *  vinculados. FE deriva cores brand via mapeamento level → color. Default []
   *  quando plansCount=0. Renderiza N dots coloridos na coluna EM PLANOS
   *  (Image #34). */
  topPlanLevels?: number[];
  /** Contrato ESCR-product-listing-linked-plans — 1 item por plano
   *  `published`/`approval_pending` ativo que inclui o produto (mesmo critério
   *  de `plansCount`). Ordenado por `level ASC, planName ASC`. Alimenta o
   *  popover "EM PLANOS". Opcional pra compat com responses legadas; `[]`
   *  quando não há planos. */
  plans?: LinkedPlanSummary[];
  /** ESCR-204.1 — produto oculto: entra no cálculo do subtotal mas NÃO aparece
   *  em cotação/proposta/email pro consultor/cliente. Caso de uso: taxa
   *  administrativa. Backend filtra `isHidden=true` em endpoints públicos;
   *  admin recebe todos com a flag. Opcional pra compat com endpoints legados. */
  isHidden?: boolean;
  /** F3 (ESCR-210.5): descrição visível só pro consultor (separada da pública). */
  descriptionForConsultant?: string | null;
  /** ESCR-206.1 — workflow state machine SD-1. Backend backfilled rows existentes
   *  como `published`+`version=1` (migration 0090). Opcional aqui pra compat com
   *  endpoints legados que ainda não retornam o campo. */
  status?: 'draft' | 'approval_pending' | 'published' | 'rejected' | 'archived';
  version?: number;
  submittedBy?: string | null;
  submittedAt?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  publishedBy?: string | null;
  publishedAt?: string | null;
  rejectedBy?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  archivedBy?: string | null;
  archivedAt?: string | null;
  /** Phase 08 Plan 06 (ESCR-218) — enrichment CS ativo vinculado à entidade (Plan 01 backend). */
  activeChangeSet?: ChangeSetActiveEnrichment | null;
}

// ESCR-180.17 (Phase 07 Plan 03 — 2026-05-15) — interface `PricingProductPrice`
// removida. Tabela `product_prices` foi descontinuada (legacy A) — preço de
// variante vive em `variant_price_category` (M:N + `categoryId IS NULL` =
// preço global). Endpoints CRUD `/admin/pricing-tables/products/:id/prices`
// retornam HTTP 404 pós-Plan 02. Ver `docs/contracts/ESCR-180.16.md`.

/**
 * ESCR-211.16 (Cluster G Gap #22 UAT 2026-05-14) — preço por categoria
 * embutido na response de `listVariants`. Catch-all (variant_price_category
 * `categoryId IS NULL` — modo "Preço único") chega com category* = null.
 */
export interface VariantPriceByCategory {
  /** UUID da row em `variant_price_category` — usado pra casar com
   * `change_set_items.targetEntityId` quando há CS ativo de preço.
   * Phase 08 (2026-05-27): incluído no select de `listVariants` para FE
   * resolver diff inline `before → after` sem precisar de `variantId` no
   * snapshot do item (op `update` carrega só `{ price, isActive }`). */
  id: string;
  /** UUID da vehicle_category. NULL = catch-all global. */
  categoryId: string | null;
  /** Code legível ('carro', 'moto', 'caminhao'). NULL quando catch-all. */
  categoryCode: string | null;
  /** Nome humano ('Carro', 'Moto'). NULL quando catch-all. */
  categoryName: string | null;
  /** DECIMAL(12,2). */
  price: string;
}

export interface PricingProductVariant {
  id: string;
  productId: string;
  code: string;
  /** Código atual do produto correspondente no catálogo SGA Hinova. */
  sgaCode?: string | null;
  name: string;
  /**
   * Descrição pública da variante (proposta + PDF do cliente). `null` = a
   * variante não tem descrição própria e o consumidor cai na `description` do
   * produto-base. Override por categoria vive em
   * `presentationByCategory[categoria].description`.
   * Contrato: `docs/contracts/ESCR-variant-description.md`.
   */
  description?: string | null;
  presentationByCategory?: ProductPresentationByCategory | null;
  // ESCR-180.17 (Phase 07 Plan 03 — 2026-05-15) — campo `priceMonthly`
  // removido. Preço de variante vive em `variant_price_category` (M:N + global
  // via `categoryId IS NULL`) — disponível em `pricesByCategory[]` no list.
  // Schema TS já dropou a coluna em ESCR-180.16; coluna DB sai em migration
  // 0119 do Plan 04 (ESCR-180.18).
  isOptional: boolean;
  displayOrder: number;
  isActive: boolean;
  /** ESCR-206.1 — workflow SD-1 (backfilled como `published`+v1). */
  status?: 'draft' | 'approval_pending' | 'published' | 'rejected' | 'archived';
  version?: number;
  submittedBy?: string | null;
  submittedAt?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  publishedBy?: string | null;
  publishedAt?: string | null;
  rejectedBy?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  archivedBy?: string | null;
  archivedAt?: string | null;
  /** F3 (ESCR-210.1): limite de indenização — DECIMAL(12,2) opcional. */
  indemnityLimit?: string | null;
  /** F3 (ESCR-210.2): soft-delete pra venda (não entra em cotação se false). */
  isAvailableForSale?: boolean;
  requiresInspectionOnAddition?: boolean;
  endorsementInspectionMode?: 'none' | 'full_vehicle' | 'directed_component';
  endorsementInspectionComponent?:
    | 'windshield'
    | 'glass_package'
    | 'headlights'
    | 'taillights'
    | 'mirrors'
    | null;
  endorsementWaitingPeriodDays?: number;
  /**
   * Cluster G Gap #24 (UAT 2026-05-14) — display name longo p/ proposta/PDF
   * (ex: "Assistência 24h Premium até 500km"). Fallback no consumer: `name`.
   */
  displayName?: string | null;
  /**
   * ESCR-211.16 Cluster G Gap #22 — populado apenas em listVariants
   * (LEFT JOIN variant_price_category). Pode vir undefined em responses
   * de mutação (create/update) — nesse caso FE re-fetch o list.
   */
  pricesByCategory?: VariantPriceByCategory[];
  /**
   * Cluster J Gap #25 (ESCR-211.17 UAT 2026-05-14) — quantos planos
   * `published`/`approval_pending` ativos incluem esta variante (default OU
   * upgrade variant). Default 0. Populado pelo listVariants. */
  plansCount?: number;
  /**
   * Cluster J Gap #25 — até 3 levels DISTINCT (ASC) dos planos vinculados à
   * variante. FE deriva cores brand via mapeamento level → color. Default [].
   */
  topPlanLevels?: number[];
  /**
   * Contrato ESCR-product-listing-linked-plans — 1 item por plano
   * `published`/`approval_pending` ativo em que esta variante é a inclusa
   * (default) ou um upgrade. Subconjunto do `plans` do produto pai. Ordenado
   * por `level ASC, planName ASC`. Alimenta o popover "EM PLANOS". `[]` quando
   * não há planos; opcional pra compat com responses legadas.
   */
  plans?: LinkedPlanSummary[];
  /**
   * Phase 08.2 Plan 01 backend (ESCR-218 paridade Pilar 1) — enrichment CS ativo
   * vinculado à variante. Backend listVariants faz LEFT JOIN
   * change_set_items.target_entity_id quando rootEntityType='product_variant'.
   */
  activeChangeSet?: ChangeSetActiveEnrichment | null;
}

export interface PricingPlan {
  id: string;
  code: string;
  name: string;
  /**
   * ESCR-203.1 — agora obrigatório (backend `plans.category_id` NOT NULL).
   * Mantemos `| null` apenas pra responses legadas durante a transição;
   * sera estreitado quando todos os clientes baterem na nova migration.
   */
  categoryId: string | null;
  level: number;
  franchiseApplies: boolean;
  endorsementProtectionClass?: 'robbery_theft' | 'complete' | 'other';
  isActive: boolean;
  /**
   * ESCR-221.2 — modalidade de preço. `'fixed'` = mensalidade fechada
   * (`fixedMonthlyPrice`), ignora motor/FIPE/franquia. Default `'engine'`.
   */
  pricingMode?: 'engine' | 'fixed';
  /** ESCR-221.2 — mensalidade fixa como string DECIMAL (ex: "89.90"); null no modo engine. */
  fixedMonthlyPrice?: string | null;
  /** ESCR-288.1 — piso comercial final do plano; null desativa o piso específico. */
  minimumMonthlyPrice: string | null;
  createdAt: string;
  updatedAt: string;
  /** ESCR-206.1 — workflow SD-1 (backfill rows existentes como `published`+v1). */
  status?: 'draft' | 'approval_pending' | 'published' | 'rejected' | 'archived';
  version?: number;
  submittedBy?: string | null;
  submittedAt?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  publishedBy?: string | null;
  publishedAt?: string | null;
  rejectedBy?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  archivedBy?: string | null;
  archivedAt?: string | null;
  /** F3 (ESCR-207.4): plano recomendado (max 1 por categoria, enforced server). */
  isRecommended?: boolean;
  /** F3 (ESCR-209.3): vigência. `validTo=null` = indefinido. */
  validFrom?: string | null;
  validTo?: string | null;
  /** F3 (ESCR-209.5): tags multi-value + descrição interna (não vai pro consultor). */
  tags?: string[];
  internalDescription?: string | null;
  // ─── BACK-2 ESCR-211.10 — LEFT JOIN users + vehicle_categories ──────────
  /**
   * BACK-2 — `users.name` via LEFT JOIN (plans.updatedBy → users.id).
   * NULL quando `updatedBy IS NULL` (plano nunca atualizado) OU usuário
   * deletado (FK ON DELETE SET NULL). Destrava footer literal do card T4
   * ("atualizado 28/04 por Júlia Castro") sem N+1 fetch separado.
   */
  updatedByName?: string | null;
  /** BACK-2 — `users.email` via LEFT JOIN; pareado com updatedByName. */
  updatedByEmail?: string | null;
  /**
   * BACK-2 — `vehicle_categories.name` via LEFT JOIN (plans.categoryId →
   * vehicle_categories.id). NOT NULL na prática (categoryId NOT NULL desde
   * ESCR-203.1) mas tipado nullable por defesa (LEFT JOIN preserva rows).
   */
  categoryName?: string | null;
  /** BACK-2 — `vehicle_categories.code` (ex: "carro", "moto"); pareado com categoryName. */
  categoryCode?: string | null;
  // ─── BACK-3 ESCR-211.11 — aggregate modeCounts via correlated subqueries ──
  /**
   * BACK-3 — COUNT(*) de plan_products linkados ao plano (sem filtrar
   * is_active). Elimina N+1 do FE PlanCardCounters que fazia
   * `usePlanProducts(planId)` por card.
   */
  totalProductsCount?: number;
  /** BACK-3 — COUNT(*) onde inclusion_type='required'. */
  requiredCount?: number;
  /**
   * BACK-3 — COUNT(*) onde inclusion_type='required' AND >=1 critério de
   * aplicabilidade preenchido (fipe_min/max OR uf_codes/city_ids/model_ids/
   * subgroup_ids não-vazio). É SUBSET de requiredCount (D-24 Opção A —
   * "Obrigatório condicional" pseudo-derivado server-side).
   * Invariant: conditionalCount <= requiredCount.
   */
  conditionalCount?: number;
  /** BACK-3 — COUNT(*) onde inclusion_type='optional'. */
  optionalCount?: number;
  /** BACK-3 — COUNT(*) onde inclusion_type='not_available'. */
  notAvailableCount?: number;
  /** Phase 08 Plan 06 (ESCR-218) — enrichment CS ativo vinculado ao plano (Plan 01 backend). */
  activeChangeSet?: ChangeSetActiveEnrichment | null;
}

/** ESCR-201.1 — tipos de inclusão de um produto num plano. */
export type PlanProductInclusionType = 'included' | 'optional' | 'required' | 'not_available';

export interface PricingPlanProduct {
  /** ESCR-201.1 — PK UUID dedicada (múltiplas rows por par planId+productId). */
  id?: string;
  planId: string;
  productId: string;
  inclusionType?: PlanProductInclusionType;
  displayOrder: number;
  /** ESCR-201.1 — variante incluída por default no plano (forçado null em not_available). */
  defaultVariantId?: string | null;
  /** ESCR-201.1 — UUIDs das variantes que o consultor pode adicionar como upgrade. */
  allowedUpgradeVariantIds?: string[] | null;
  fipeMin?: string | null;
  fipeMax?: string | null;
  ufCodes?: string[] | null;
  cityIds?: string[] | null;
  modelIds?: string[] | null;
  subgroupIds?: string[] | null;
  isActive?: boolean;
  createdAt: string;
  updatedAt?: string;
  /**
   * Phase 08.2 Plan 01 backend (ESCR-218 paridade Pilar 1) — enrichment CS ativo
   * vinculado a esta regra plan_products. Backend listPlanProducts faz LEFT JOIN
   * change_set_items.target_entity_id quando rootEntityType='plan_product'.
   */
  activeChangeSet?: ChangeSetActiveEnrichment | null;
}

export type PricingParameterValueType = 'number' | 'string' | 'boolean' | 'json';

export interface PricingParameter {
  id: string;
  key: string;
  value: string;
  valueType: PricingParameterValueType;
  description: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Query keys ────────────────────────────────────────────────────────────

export const pricingTablesKeys = {
  all: ['admin', 'pricing-tables'] as const,
  stats: () => [...pricingTablesKeys.all, 'stats'] as const,
  baseTables: () => [...pricingTablesKeys.all, 'base-tables'] as const,
  baseEntries: (filters?: BaseEntriesListFilters) =>
    [...pricingTablesKeys.all, 'base-entries', filters] as const,
  products: () => [...pricingTablesKeys.all, 'products'] as const,
  /** Row completa de 1 produto (`GET /products/:id`) — prefixo `products()`
   *  para que a invalidação das mutations alcance o detalhe também. */
  product: (id: string) => [...pricingTablesKeys.all, 'products', 'detail', id] as const,
  // ESCR-180.17 (Phase 07 Plan 03) — key `productPrices` removida junto com
  // hook `usePricingProductPrices` (legacy A descontinuado).
  variants: (productId: string) => [...pricingTablesKeys.all, 'variants', productId] as const,
  plans: () => [...pricingTablesKeys.all, 'plans'] as const,
  /** ESCR-203.7 — paginated/filtered plans listing (categoryId + page + search). */
  plansList: (filters?: PlansListFilters) =>
    [...pricingTablesKeys.all, 'plans', 'list', filters] as const,
  /** ESCR-203.4 — count of active plans grouped by categoryId (powers tabs). */
  plansCountByCategory: () => [...pricingTablesKeys.all, 'plans', 'count-by-category'] as const,
  planProducts: (planId: string) => [...pricingTablesKeys.all, 'plan-products', planId] as const,
  parameters: () => [...pricingTablesKeys.all, 'parameters'] as const,
};

/** ESCR-203.4 — filtros do endpoint paginado `/admin/pricing-tables/plans`. */
export interface PlansListFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  /** UUID de `vehicle_categories` — se preenchido, restringe ao categoryId. */
  categoryId?: string;
  /**
   * Phase 08.2 (Plan 04 paridade Pilar 1) — pretende filtrar planos com CS
   * ativo no status indicado. Forward-compatible: o backend de `/plans` pode
   * ainda não filtrar (Plan 01 cobriu variants + plan_products); neste caso
   * o FE faz fallback client-side via `plan.activeChangeSet.status`.
   */
  csStatus?: 'draft' | 'approval_pending';
}

/** ESCR-203.4 — shape de `/admin/pricing-tables/plans/count-by-category`. */
export type PlansCountByCategory = Array<{ categoryId: string; count: number }>;

const BASE = '/admin/pricing-tables';

// ─── Stats ────────────────────────────────────────────────────────────────
// `usePricingTablesStats` + `PricingTablesStats` removed in pricing-admin-v2 W5 cleanup.
// Legacy consumer (PricingTables/PricingTablesPage.tsx) was deleted. Admin/Pricing/*
// uses domain-specific hooks (useAdjustmentCounts, etc.) or `GET /stats` via a future
// `usePricingAdminStats` hook when dashboard wiring is implemented.

// ─── Base Tables ──────────────────────────────────────────────────────────

// Backwards-compat shim — pages here still expect array shape. Backend now
// returns { count, rows } per PAGINATION-STANDARD.md; we extract rows and
// fetch with limit=100 (PaginationQueryDto max) to avoid silently dropping
// data on small catalogues. Proper pagination UI is a follow-up task.
export function usePricingBaseTables() {
  return useQuery({
    queryKey: pricingTablesKeys.baseTables(),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<{ count: number; rows: PricingBaseTable[] }>>(
        `${BASE}/base-tables`,
        { params: { limit: 100 } },
      );
      return res.data.data.rows;
    },
  });
}

export function useDuplicateBaseTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<ApiSuccess<PricingBaseTable>>(
        `${BASE}/base-tables/${id}/duplicate`,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingTablesKeys.all });
    },
  });
}

export function useDeleteBaseTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await apiClient.delete(`${BASE}/base-tables/${id}`, { data: { reason } });
      return { id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingTablesKeys.all });
    },
  });
}

// ─── Base Entries ─────────────────────────────────────────────────────────

export interface BaseEntriesListFilters {
  /**
   * Phase 08.1 (2026-05-25) — `categoryId` substitui `tableId` pós-DROP de
   * `pricing_base_tables`. `tableId` mantido como alias compat: backend
   * aceita ambos durante a transição (futuramente sai do FE de vez).
   */
  categoryId?: string;
  tableId?: string;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'fipeMin' | 'fm' | 'baseRateio';
  sortOrder?: 'asc' | 'desc';
  /**
   * Filtra entries com item próprio em um CS naquele status. Backend faz
   * EXISTS em change_set_items.target_entity_id (cascata FM não conta).
   */
  csStatus?: 'draft' | 'approval_pending';
}

export interface PaginatedBaseEntries {
  items: PricingBaseEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const DEFAULT_BASE_ENTRIES_LIMIT = 50;

/**
 * Paginated list of base entries — see PAGINATION-STANDARD.md.
 * Backend returns `{ count, rows }`; this hook reshapes into PaginatedResponse
 * for the consumer pages.
 */
export function usePricingBaseEntries(filters?: BaseEntriesListFilters) {
  return useQuery({
    queryKey: pricingTablesKeys.baseEntries(filters),
    queryFn: async (): Promise<PaginatedBaseEntries> => {
      const params: Record<string, string | number | undefined> = {};
      if (filters?.categoryId) params.categoryId = filters.categoryId;
      if (filters?.tableId) params.tableId = filters.tableId;
      if (filters?.page) params.page = filters.page;
      if (filters?.limit) params.limit = filters.limit;
      if (filters?.search) params.search = filters.search;
      if (filters?.sortBy) params.sortBy = filters.sortBy;
      if (filters?.sortOrder) params.sortOrder = filters.sortOrder;
      if (filters?.csStatus) params.csStatus = filters.csStatus;

      const res = await apiClient.get<ApiSuccess<{ count: number; rows: PricingBaseEntry[] }>>(
        `${BASE}/base-entries`,
        { params },
      );

      const raw = res.data.data;
      const limit = filters?.limit ?? DEFAULT_BASE_ENTRIES_LIMIT;
      return {
        items: raw.rows,
        total: raw.count,
        page: filters?.page ?? 1,
        limit,
        totalPages: Math.max(1, Math.ceil(raw.count / limit)),
      };
    },
    // Enabled quando categoryId OU tableId (legacy) for fornecido — ou
    // quando ambos são undefined (consumer pode querer listar TUDO).
    enabled: filters?.categoryId
      ? !!filters.categoryId
      : filters?.tableId
        ? !!filters.tableId
        : true,
  });
}

export interface BulkImportEntry {
  // ESCR-179.13 (Phase 06 — Pilar 1 sem subgroup) — `subgroupId` removido em
  // 2026-05-15. CSVs antigos com coluna `subgrupo` continuam aceitos
  // silenciosamente (parser ignora; ver contrato ESCR-179.12 § 4).
  fipeRangeId: string;
  fm: string;
  baseRateio: string;
}

export function useBulkImportBaseEntries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      tableId: string;
      entries: BulkImportEntry[];
      replaceAll?: boolean;
    }) => {
      const res = await apiClient.post<ApiSuccess<{ replaced: number; inserted: number }>>(
        `${BASE}/base-tables/${input.tableId}/bulk-import`,
        { entries: input.entries, replaceAll: input.replaceAll ?? true },
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingTablesKeys.all });
    },
  });
}

// ─── Products ─────────────────────────────────────────────────────────────

// Backend PaginationQueryDto cap é 100/página. Quando catálogo passa de 100
// produtos, paginação é obrigatória — antes esse hook trazia só os 100
// primeiros e consumers (productMap em PlansCards) entravam em fallback UUID
// pros produtos fora da página.
const PRODUCTS_PAGE_SIZE = 100;
// 2000 produtos antes de parar — guard defensivo contra catálogo gigante.
const PRODUCTS_MAX_PAGES = 20;

export function usePricingProducts() {
  const query = useInfiniteQuery({
    queryKey: pricingTablesKeys.products(),
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const res = await apiClient.get<ApiSuccess<{ count: number; rows: PricingProduct[] }>>(
        `${BASE}/products`,
        { params: { limit: PRODUCTS_PAGE_SIZE, page: pageParam } },
      );
      return res.data.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((sum, p) => sum + p.rows.length, 0);
      if (fetched >= lastPage.count) return undefined;
      if (lastPage.rows.length < PRODUCTS_PAGE_SIZE) return undefined;
      if (allPages.length >= PRODUCTS_MAX_PAGES) return undefined;
      return allPages.length + 1;
    },
  });

  const { hasNextPage, isFetchingNextPage, isLoading, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isLoading) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isLoading, fetchNextPage]);

  // Achata as páginas e devolve no shape antigo (data: PricingProduct[]) pra
  // não quebrar consumers existentes (ProductSelect, PlansCards, Pilar2MatrixTab etc).
  const data = query.data ? query.data.pages.flatMap((p) => p.rows) : undefined;
  return { ...query, data };
}

/**
 * ESCR-180.9 — Paginated variant of `usePricingProducts`. Returns the raw
 * `{count, rows}` envelope so the UI can wire `<DataTablePagination>` and
 * URL state. The legacy `usePricingProducts()` (no-args) is kept for
 * consumers that just need the full list (variants/plans pickers).
 */
export interface UsePricingProductsPaginatedFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  /** Backend currently expects no `is_active` filter — kept here for forward
   *  compat once the endpoint exposes it. Until then, frontend filters
   *  in-memory by `isActive`. */
  status?: 'active' | 'inactive' | 'all';
  /** BACK-1 (ESCR-211.9) — filtro por categoria (Carro/Moto/Caminhão...).
   *  Backend aplica EXISTS subquery sobre `variant_price_category`:
   *  retorna apenas products com >=1 variante ativa precificada para a
   *  categoria. NULL `category_id` na tabela conta como catch-all global
   *  (ESCR-201.11 multi-cat). Antes (ESCR-180.2) o backend ignorava o filtro
   *  silently — comportamento mudou em 2026-05-14. Ver contrato
   *  `docs/contracts/ESCR-211.9.md`. */
  categoryId?: string;
  /**
   * Filtro server-side: products com item próprio em CS no status indicado.
   * Backend faz EXISTS em change_set_items.target_entity_type='product'.
   * Paridade Pilar 1 (RateioTab) + Pilar 3 (RulesTable). Chips
   * "Em rascunho" / "Aguardando aprovação".
   */
  csStatus?: 'draft' | 'approval_pending';
}

export function usePricingProductsPaginated(filters?: UsePricingProductsPaginatedFilters) {
  // Backend canonical param é `limit` (PaginationQueryDto cap 100). `pageSize`
  // é alias deprecated — não mandar pra evitar dupla validação no 422.
  const params: Record<string, string | number> = { limit: filters?.pageSize ?? 20 };
  if (filters?.page) params.page = filters.page;
  if (filters?.search) params.search = filters.search;
  if (filters?.categoryId) params.categoryId = filters.categoryId;
  if (filters?.csStatus) params.csStatus = filters.csStatus;
  return useQuery({
    queryKey: [...pricingTablesKeys.products(), 'paginated', filters ?? {}] as const,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<{ count: number; rows: PricingProduct[] }>>(
        `${BASE}/products`,
        { params },
      );
      return res.data.data;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * Auto-paginated variant — usa useInfiniteQuery e dispara fetchNextPage
 * automaticamente até esgotar `count`. Backend cap é 100/página
 * (PaginationQueryDto). Consumidores (drawer bulk-add, preview de duplicate)
 * querem a lista completa client-side; antes pediam `limit=200` e levavam 422.
 *
 * Retorna o shape achatado (`rows`, `count`) para minimizar mudança nos
 * consumidores existentes. Reusa `PRODUCTS_PAGE_SIZE`/`PRODUCTS_MAX_PAGES`
 * declaradas acima junto de `usePricingProducts`.
 */

export interface UsePricingProductsAllByCategoryOptions {
  categoryId?: string;
  enabled?: boolean;
}

export function usePricingProductsAllByCategory(
  options: UsePricingProductsAllByCategoryOptions = {},
) {
  const { categoryId, enabled = true } = options;
  const query = useInfiniteQuery({
    queryKey: [...pricingTablesKeys.products(), 'all-by-category', categoryId ?? null] as const,
    enabled,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string | number> = {
        limit: PRODUCTS_PAGE_SIZE,
        page: pageParam,
      };
      if (categoryId) params.categoryId = categoryId;
      const res = await apiClient.get<ApiSuccess<{ count: number; rows: PricingProduct[] }>>(
        `${BASE}/products`,
        { params },
      );
      return res.data.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((sum, p) => sum + p.rows.length, 0);
      if (fetched >= lastPage.count) return undefined;
      if (lastPage.rows.length < PRODUCTS_PAGE_SIZE) return undefined;
      if (allPages.length >= PRODUCTS_MAX_PAGES) return undefined;
      return allPages.length + 1;
    },
  });

  // Auto-fetch das páginas restantes em background. Roda em effect pra evitar
  // setState durante render. TanStack dedup chamadas concorrentes, então o
  // re-trigger no próximo render é seguro até hasNextPage virar false.
  const { hasNextPage, isFetchingNextPage, isLoading, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isLoading) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isLoading, fetchNextPage]);

  const rows = query.data?.pages.flatMap((p) => p.rows) ?? [];
  const count = query.data?.pages[0]?.count ?? 0;

  return {
    rows,
    count,
    isLoading: query.isLoading,
    isFetchingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage ?? false,
    isError: query.isError,
    error: query.error,
  };
}

/**
 * `GET /admin/pricing-tables/products/:id` — row completa do produto.
 *
 * `listProducts` usa projeção explícita e expõe um subconjunto das colunas; um
 * form de edição precisa da row inteira, senão reenvia como `null` no PUT
 * (replace) o campo que a listagem não trouxe.
 */
export function usePricingProduct(id?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: pricingTablesKeys.product(id ?? ''),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<PricingProduct>>(`${BASE}/products/${id}`);
      return res.data.data;
    },
    enabled: (options?.enabled ?? true) && Boolean(id),
  });
}

export function useCreatePricingProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      code: string;
      name: string;
      description?: string;
      displayOrder?: number;
      isActive?: boolean;
      /** ESCR-204.1 — produto oculto (default false). */
      isHidden?: boolean;
      /** F3 (ESCR-210.5) — descrição visível só pro consultor. */
      descriptionForConsultant?: string | null;
      /** D-24 — tipologia funcional do produto. */
      functionalType?: 'coverage' | 'service' | 'benefit' | 'fee';
      /** D-24 — tipologia comercial do produto. */
      commercialType?: 'priceable' | 'mandatory_free' | 'included' | 'informational';
      /** ESCR-180.1 — rótulo pt-BR do tipo. `null` limpa a classificação. */
      type?: 'cobertura' | 'servico' | 'beneficio' | 'taxa' | null;
      /** Modelo de exibição — grupo de exibição livre (≤80). */
      displayGroup?: string | null;
      /** Modelo de exibição — observação só-ADM (nunca vai a cliente/PDF). */
      internalNotes?: string | null;
      /** Modelo de exibição — subitens informativos (só exibição). */
      subitems?: ProductSubitem[];
      /** Apresentação pública por código de categoria. */
      presentationByCategory?: ProductPresentationByCategory | null;
    }) => {
      const res = await apiClient.post<ApiSuccess<PricingProduct>>(`${BASE}/products`, input);
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: pricingTablesKeys.all }),
  });
}

export function useUpdatePricingProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      code?: string;
      name?: string;
      description?: string | null;
      displayOrder?: number;
      isActive?: boolean;
      /** ESCR-204.1 — produto oculto. */
      isHidden?: boolean;
      /** F3 (ESCR-210.5) — descrição visível só pro consultor. */
      descriptionForConsultant?: string | null;
      /** D-24 — tipologia funcional do produto. */
      functionalType?: 'coverage' | 'service' | 'benefit' | 'fee';
      /** D-24 — tipologia comercial do produto. */
      commercialType?: 'priceable' | 'mandatory_free' | 'included' | 'informational';
      /** ESCR-180.1 — rótulo pt-BR do tipo. `null` limpa a classificação. */
      type?: 'cobertura' | 'servico' | 'beneficio' | 'taxa' | null;
      /** Modelo de exibição — grupo de exibição livre (≤80). `null` limpa. */
      displayGroup?: string | null;
      /** Modelo de exibição — observação só-ADM. `null` limpa. */
      internalNotes?: string | null;
      /** Modelo de exibição — subitens informativos. `[]` limpa. */
      subitems?: ProductSubitem[];
      /** Apresentação pública por código de categoria. */
      presentationByCategory?: ProductPresentationByCategory | null;
    }) => {
      const { id, ...body } = input;
      const res = await apiClient.put<ApiSuccess<PricingProduct>>(`${BASE}/products/${id}`, body);
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: pricingTablesKeys.all }),
  });
}

export function useDeletePricingProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await apiClient.delete(`${BASE}/products/${id}`, { data: { reason } });
      return { id };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: pricingTablesKeys.all }),
  });
}

// ─── Product Prices ───────────────────────────────────────────────────────
// ESCR-180.17 (Phase 07 Plan 03 — 2026-05-15) — hook `usePricingProductPrices`
// removido. Endpoint `GET /admin/pricing-tables/products/:id/prices` retorna
// HTTP 404 pós-Plan 02 (ESCR-180.16). Substituto: `useVariantPrices(variantId)`
// + `useReplaceVariantPrices` em `./usePilar2` (matriz `variant_price_category`
// M:N + global via `categoryId IS NULL`). Ver `docs/contracts/ESCR-180.16.md`.

// ─── Product Variants ─────────────────────────────────────────────────────

export function usePricingVariants(productId: string) {
  return useQuery({
    queryKey: pricingTablesKeys.variants(productId),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<{ count: number; rows: PricingProductVariant[] }>>(
        `${BASE}/products/${productId}/variants`,
        { params: { limit: 100 } },
      );
      return res.data.data.rows;
    },
    enabled: !!productId,
  });
}

/** F3 (ESCR-210.1/.2) — campos novos na variante (opcional pra retro-compat). */
export interface VariantF3Input {
  indemnityLimit?: string | null;
  isAvailableForSale?: boolean;
  endorsementWaitingPeriodDays?: number;
}

/**
 * ESCR-211.16 Cluster G Gap #24 — input shape para `displayName`. Separado
 * de VariantF3Input pra mantermos o agrupamento conceitual por feature.
 */
export interface VariantClusterGInput {
  displayName?: string | null;
}

export interface SgaProductLookup {
  found: boolean;
  product: {
    code: string;
    description: string | null;
    vehicleType: string | null;
    amount: string | null;
    status: string | null;
    supplierCode: string | null;
    supplierName: string | null;
    classificationCode: string | null;
  } | null;
}

export function useLookupSgaProduct() {
  return useMutation({
    mutationFn: async (code: string) => {
      const res = await apiClient.get<ApiSuccess<SgaProductLookup>>(
        `/admin/integrations/hinova/products/${encodeURIComponent(code)}`,
      );
      return res.data.data;
    },
  });
}

export function useCreateVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: {
        productId: string;
        code: string;
        name: string;
        sgaCode?: string | null;
        // ESCR-180.17 (Phase 07 Plan 03) — campo `priceMonthly` removido do
        // payload. DTO backend (`UpsertVariantDto`) tem `forbidNonWhitelisted`
        // — payload contendo `priceMonthly` é rejeitado com HTTP 400. Preço
        // de variante vive em `variant_price_category`: chamar
        // `useReplaceVariantPrices` após o create pra popular a matriz.
        isOptional?: boolean;
        displayOrder?: number;
        /**
         * `product_variants.is_active` (default true no backend). `false` tira a
         * variante do motor inteiro — liveness da cotação, lista de opcionais e
         * persistência filtram por este campo.
         */
        isActive?: boolean;
        /**
         * Descrição pública da variante. Omitir (`undefined`) = backend grava
         * `null` e o consumidor cai na descrição do produto-base.
         */
        description?: string | null;
        presentationByCategory?: ProductPresentationByCategory | null;
      } & VariantF3Input &
        VariantClusterGInput,
    ) => {
      const { productId, ...body } = input;
      const res = await apiClient.post<ApiSuccess<PricingProductVariant>>(
        `${BASE}/products/${productId}/variants`,
        body,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingTablesKeys.all });
      // Phase 08.2 Plan 06 — backend pode abrir/estender CS automático ao
      // mutar variante via SD-1 (ESCR-214/-215). Invalida cache pricing CS
      // pra refrescar enrichment `activeChangeSet` + badge/banner.
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

export function useUpdateVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: {
        id: string;
        code?: string;
        sgaCode?: string | null;
        name?: string;
        // ESCR-180.17 (Phase 07 Plan 03) — campo `priceMonthly` removido do
        // payload. Preço de variante vive em `variant_price_category` via
        // `useReplaceVariantPrices`.
        isOptional?: boolean;
        displayOrder?: number;
        isActive?: boolean;
        /**
         * Descrição pública da variante. PUT é PATCH-style: `undefined` omite a
         * chave e PRESERVA o valor gravado; `null` limpa a descrição.
         */
        description?: string | null;
        presentationByCategory?: ProductPresentationByCategory | null;
      } & VariantF3Input &
        VariantClusterGInput,
    ) => {
      const { id, ...body } = input;
      const res = await apiClient.put<ApiSuccess<PricingProductVariant>>(
        `${BASE}/variants/${id}`,
        body,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingTablesKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

export function useDeleteVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await apiClient.delete(`${BASE}/variants/${id}`, { data: { reason } });
      return { id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingTablesKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

// ─── Plans ────────────────────────────────────────────────────────────────

export function usePricingPlans() {
  return useQuery({
    queryKey: pricingTablesKeys.plans(),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<{ count: number; rows: PricingPlan[] }>>(
        `${BASE}/plans`,
        { params: { limit: 100 } },
      );
      return res.data.data.rows;
    },
  });
}

/**
 * ESCR-203.7 — listagem paginada de planos com filtro opcional por categoria
 * veicular. Espelha o padrão de `usePricingProductsPaginated`. Retorna
 * `{ count, rows }` (não desempacotado) pra UI calcular totalPages.
 *
 * Default `pageSize` = 24 (card grid 3 colunas × 8 linhas).
 */
export function usePricingPlansPaginated(filters: PlansListFilters = {}) {
  const page = Math.max(1, filters.page ?? 1);
  const pageSize = Math.max(1, filters.pageSize ?? 24);
  return useQuery({
    queryKey: pricingTablesKeys.plansList({ page, pageSize, ...filters }),
    queryFn: async () => {
      const params: Record<string, unknown> = {
        page,
        limit: pageSize,
      };
      if (filters.search) params.search = filters.search;
      if (filters.categoryId) params.categoryId = filters.categoryId;
      // Phase 08.2 paridade Pilar 1 — passa csStatus para o backend; quando
      // o endpoint ainda não suportar, o param vira no-op (DTO ignora).
      if (filters.csStatus) params.csStatus = filters.csStatus;
      const res = await apiClient.get<ApiSuccess<{ count: number; rows: PricingPlan[] }>>(
        `${BASE}/plans`,
        { params },
      );
      return res.data.data;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * ESCR-203.4 — contagem de planos ativos agrupados por `categoryId`.
 * Categorias sem nenhum plano não aparecem no array — a UI deve fazer outer
 * join com `useVehicleCategories()` pra renderizar tab com `0`.
 */
export function usePlansCountByCategory() {
  return useQuery({
    queryKey: pricingTablesKeys.plansCountByCategory(),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<PlansCountByCategory>>(
        `${BASE}/plans/count-by-category`,
      );
      return res.data.data;
    },
    // Mais estável — listagem invalida; revalida só na invalidação por
    // mutação de plan (create/update/delete).
    staleTime: 5 * 60 * 1000,
  });
}

/** F3 ESCR-209.3/209.5 — campos opcionais de vigência + tags + descrição interna. */
export interface PlanF3Input {
  validFrom?: string | null;
  validTo?: string | null;
  tags?: string[];
  internalDescription?: string | null;
  /**
   * ESCR-221.2 — modalidade de preço. `'fixed'` exige `fixedMonthlyPrice > 0`
   * e `franchiseApplies=false` (D-5, validado no backend). Default `'engine'`.
   */
  pricingMode?: 'engine' | 'fixed';
  /** ESCR-221.2 — mensalidade fixa como string DECIMAL (ex: "89.90"). */
  fixedMonthlyPrice?: string | null;
  /** ESCR-288.1 — piso comercial final como string DECIMAL (ex: "108.47"). */
  minimumMonthlyPrice?: string | null;
  endorsementProtectionClass?: 'robbery_theft' | 'complete' | 'other';
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: {
        code: string;
        name: string;
        /** ESCR-203.1 — categoryId obrigatório no backend (NOT NULL). */
        categoryId: string;
        level?: number;
        franchiseApplies?: boolean;
      } & PlanF3Input,
    ) => {
      const res = await apiClient.post<ApiSuccess<PricingPlan>>(`${BASE}/plans`, input);
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: pricingTablesKeys.all }),
  });
}

/**
 * Resultado de `PUT /plans/:id`. O backend (pricing-tables.service.ts
 * `updatePlan`) responde 200 com um de três shapes discriminados por `status`:
 *   - plano rascunho/approval_pending → UPDATE in-place, retorna a row do plano
 *     (sem campo `status` de discriminação — é o `PricingPlan` cru).
 *   - plano publicado COM diff real → abre Change Set "Atualizar plano X"
 *     (precisa aprovação+publicação), retorna `{ status: 'change_set_created',
 *     pendingChangeSetId }`.
 *   - plano publicado SEM diff real → no-op, retorna `{ status: 'no_changes',
 *     plan }`. Acontece quando `formState.isDirty` do RHF marca dirty por
 *     diferença cosmética (data normalizada) mas o diff do backend é vazio.
 * Consumidores discriminam por `status` para rotear corretamente (CS criado ≠
 * salvo in-place ≠ nada mudou).
 */
export type UpdatePlanResult =
  | PricingPlan
  | { status: 'change_set_created'; pendingChangeSetId: string }
  | { status: 'no_changes'; plan: PricingPlan };

export function isUpdatePlanChangeSet(
  result: UpdatePlanResult,
): result is { status: 'change_set_created'; pendingChangeSetId: string } {
  return (
    'status' in result &&
    result.status === 'change_set_created' &&
    typeof (result as { pendingChangeSetId?: unknown }).pendingChangeSetId === 'string'
  );
}

export function isUpdatePlanNoChanges(
  result: UpdatePlanResult,
): result is { status: 'no_changes'; plan: PricingPlan } {
  return 'status' in result && result.status === 'no_changes';
}

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: {
        id: string;
        code?: string;
        name?: string;
        level?: number;
        franchiseApplies?: boolean;
        categoryId?: string | null;
        isActive?: boolean;
      } & PlanF3Input,
    ) => {
      const { id, ...body } = input;
      const res = await apiClient.put<ApiSuccess<UpdatePlanResult>>(`${BASE}/plans/${id}`, body);
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: pricingTablesKeys.all }),
  });
}

/**
 * F3 ESCR-207.4 — toggle `is_recommended` (max 1 por categoria; server enforces).
 *
 * Rota live em `pilar2-refinements.controller.ts` (`@Controller('admin')`),
 * NÃO em `pricing-tables.controller.ts`. Por isso usa `/admin/plans/...` em
 * vez de `${BASE}/plans/...` (`/admin/pricing-tables/plans/...` daria 404).
 */
export function useToggleRecommendPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (planId: string) => {
      const res = await apiClient.patch<ApiSuccess<PricingPlan>>(
        `/admin/plans/${planId}/recommend`,
      );
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: pricingTablesKeys.all }),
  });
}

/**
 * Resultado de `DELETE /plans/:id`. O backend (pricing-tables.controller.ts)
 * sempre responde 200 com um de dois shapes:
 *   - plano rascunho → DELETE direto, retorna `{ id }`
 *   - plano publicado → abre Change Set "Remover plano X" (precisa
 *     aprovação+publicação), retorna `{ pendingChangeSetId, status:
 *     'change_set_created' }`
 * Consumidores discriminam pela presença de `pendingChangeSetId` para dar o
 * feedback correto (CS criado ≠ exclusão efetivada).
 */
export type DeletePlanResult =
  | { id: string }
  | { pendingChangeSetId: string; status: 'change_set_created' };

export function isDeletePlanChangeSet(
  result: DeletePlanResult,
): result is { pendingChangeSetId: string; status: 'change_set_created' } {
  return 'pendingChangeSetId' in result && typeof result.pendingChangeSetId === 'string';
}

export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const res = await apiClient.delete<ApiSuccess<DeletePlanResult>>(`${BASE}/plans/${id}`, {
        data: { reason },
      });
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: pricingTablesKeys.all }),
  });
}

// ─── Plan ↔ Products bundle ───────────────────────────────────────────────

export function usePlanProducts(planId: string) {
  return useQuery({
    queryKey: pricingTablesKeys.planProducts(planId),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<{ count: number; rows: PricingPlanProduct[] }>>(
        `${BASE}/plans/${planId}/products`,
        { params: { limit: 100 } },
      );
      return res.data.data.rows;
    },
    enabled: !!planId,
  });
}

export function useAddProductToPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      planId: string;
      productId: string;
      inclusionType?: PlanProductInclusionType;
      displayOrder?: number;
    }) => {
      const { planId, ...body } = input;
      const res = await apiClient.post<ApiSuccess<PricingPlanProduct>>(
        `${BASE}/plans/${planId}/products`,
        body,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingTablesKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

/**
 * ESCR-201.3 / 201.10 — DTO completo de uma regra `plan_products`. Permite
 * múltiplas rows por par (planId, productId), cada uma com critérios distintos.
 */
export interface UpsertPlanProductRuleInput {
  productId: string;
  inclusionType?: PlanProductInclusionType;
  defaultVariantId?: string | null;
  allowedUpgradeVariantIds?: string[];
  /**
   * Critérios de aplicabilidade. No PATCH `/plans/:planId/products/:id` o
   * backend faz merge: campo AUSENTE preserva o valor atual da row; `null`
   * (escalares) e `[]` (arrays) limpam o critério. Para zerar um critério é
   * obrigatório mandá-lo explicitamente — omitir mantém o valor antigo.
   */
  fipeMin?: string | null;
  fipeMax?: string | null;
  ufCodes?: string[];
  cityIds?: string[];
  modelIds?: string[];
  subgroupIds?: string[];
  displayOrder?: number;
  isActive?: boolean;
}

/**
 * ESCR-201.10 — cria uma nova regra (`plan_products` row) usando o shape novo.
 * Substitui o `useAddProductToPlan` quando precisar mais que toggle de
 * optional. Backend valida via `assertProductCompatibleWithPlanCategory`
 * (ESCR-203.3) e demais constraints.
 */
export function useCreatePlanProductRule(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertPlanProductRuleInput) => {
      const res = await apiClient.post<ApiSuccess<PricingPlanProduct>>(
        `${BASE}/plans/${planId}/products`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingTablesKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

/** ESCR-201.3 — update parcial por `id` UUID da row. */
export function useUpdatePlanProductRule(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Partial<UpsertPlanProductRuleInput>) => {
      const { id, ...body } = input;
      const res = await apiClient.patch<ApiSuccess<PricingPlanProduct>>(
        `${BASE}/plans/${planId}/products/${id}`,
        body,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingTablesKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

// ─── Wave 4 (Gap #38) — Bundle agregado de produto-no-plano ─────────────────
// Visão "1 config + N regras" do mesmo (planId, productId). Backend agrega N
// rows de plan_products em response unificada (pega config da row de menor
// displayOrder; lista todas como rules[]). Contrato:
// docs/contracts/ESCR-DEBT-PLAN-PRODUCT-CONFIG.md.

export interface PlanProductBundleConfig {
  inclusionType: PlanProductInclusionType;
  defaultVariantId: string | null;
  allowedUpgradeVariantIds: string[];
}

export interface PlanProductBundleRule {
  id: string;
  displayOrder: number;
  fipeMin: string | null;
  fipeMax: string | null;
  ufCodes: string[];
  cityIds: string[];
  modelIds: string[];
  subgroupIds: string[];
  status: 'draft' | 'approval_pending' | 'published' | 'rejected' | 'archived' | null;
}

export interface PlanProductBundle {
  productId: string;
  planId: string;
  config: PlanProductBundleConfig;
  rules: PlanProductBundleRule[];
}

export function useProductBundle(
  planId: string,
  productId: string,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: [...pricingTablesKeys.all, 'plan-product-bundle', planId, productId] as const,
    enabled: enabled && !!planId && !!productId,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<PlanProductBundle>>(
        `${BASE}/plans/${planId}/products/${productId}/bundle`,
      );
      return res.data.data;
    },
  });
}

export interface UpsertPlanProductConfigInput {
  inclusionType: PlanProductInclusionType;
  defaultVariantId?: string | null;
  allowedUpgradeVariantIds?: string[];
}

/**
 * PUT /admin/pricing-tables/plans/:planId/products/:productId/config
 *
 * Replica inclusionType + defaultVariantId + allowedUpgradeVariantIds em TODAS
 * as rows ativas de plan_products do mesmo (plan, product) — single source of
 * truth pra config do produto-no-plano. Auto-fork SD-1 quando rows estiverem
 * published. Retorna bundle agregado.
 */
export function useUpsertPlanProductConfig(planId: string, productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertPlanProductConfigInput) => {
      const res = await apiClient.put<ApiSuccess<PlanProductBundle>>(
        `${BASE}/plans/${planId}/products/${productId}/config`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingTablesKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

/** ESCR-201.3 — delete por `id` UUID da row (uma regra específica). */
export function useDeletePlanProductRule(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await apiClient.delete(`${BASE}/plans/${planId}/products/${id}`, {
        data: { reason },
      });
      return { id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingTablesKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

export function useRemoveProductFromPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { planId: string; productId: string; reason: string }) => {
      await apiClient.delete(
        `${BASE}/plans/${input.planId}/products/by-product/${input.productId}`,
        { data: { reason: input.reason } },
      );
      return input;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingTablesKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

/**
 * Phase 02 / ESCR-211.5 — Auto-fill produtos de um preset (master plan) para
 * um plano-alvo. Encapsula GET master products + N POSTs sequenciais (rate-limit
 * BE ~30 req/s + race condition em UPSERT) que antes vivia inline no
 * NewPlanWizardModal usando `apiClient` direto.
 *
 * Fix CR-02 (review 2026-05-14): `apiClient.get/post` direto em componente
 * viola api-hooks.md ("Always use TanStack Query hooks, not bare API calls").
 * Sem mutation hook, cache `usePlanProducts(targetPlanId)` ficava stale após
 * autofill → Step 3 lista vazia até refetch manual. `onSuccess` aqui invalida
 * a query key correta.
 *
 * Retorno: `{ added, failed, failedCodes }` — `failedCodes` é Set<string> de
 * error codes capturados (apiErr.response.data.code) para diagnose. CR-01
 * fix: substituiu catch {} empty por log + capture.
 */
export interface AutoFillFromPresetResult {
  added: number;
  failed: number;
  /** error codes (`apiErr.response.data.code`) capturados por POST que falhou. */
  failedCodes: Set<string>;
}

export function useAutoFillFromPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      masterPlanId: string;
      targetPlanId: string;
    }): Promise<AutoFillFromPresetResult> => {
      const masterRes = await apiClient.get<
        ApiSuccess<{ count: number; rows: PricingPlanProduct[] }>
      >(`${BASE}/plans/${input.masterPlanId}/products`, { params: { limit: 100 } });
      const masterProducts = masterRes.data.data.rows;

      let added = 0;
      let failed = 0;
      const failedCodes = new Set<string>();

      // Sequencial pra respeitar rate-limit + evitar race em UPSERT.
      for (const masterPp of masterProducts) {
        try {
          await apiClient.post(`${BASE}/plans/${input.targetPlanId}/products`, {
            productId: masterPp.productId,
            inclusionType: masterPp.inclusionType,
            defaultVariantId: masterPp.defaultVariantId,
            allowedUpgradeVariantIds: masterPp.allowedUpgradeVariantIds ?? [],
            fipeMin: masterPp.fipeMin ?? undefined,
            fipeMax: masterPp.fipeMax ?? undefined,
            ufCodes: masterPp.ufCodes ?? [],
          });
          added++;
        } catch (err) {
          // CR-01 fix: capture error code instead of silent swallow. Reporta
          // pro caller via failedCodes para diagnose por produto.
          const apiErr = err as {
            response?: { data?: { code?: string; message?: string } };
          };
          const code = apiErr.response?.data?.code ?? 'UNKNOWN_ERROR';
          failedCodes.add(code);
          failed++;
        }
      }
      return { added, failed, failedCodes };
    },
    onSuccess: (_data, variables) => {
      // Invalida o cache do plano alvo — Step 3 do wizard se baseia no
      // usePlanProducts(targetPlanId) para listar os produtos auto-filled.
      qc.invalidateQueries({
        queryKey: pricingTablesKeys.planProducts(variables.targetPlanId),
      });
    },
  });
}

// ─── Pricing Parameters ───────────────────────────────────────────────────

export function usePricingParameters() {
  return useQuery({
    queryKey: pricingTablesKeys.parameters(),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<{ count: number; rows: PricingParameter[] }>>(
        `${BASE}/parameters`,
        { params: { limit: 100 } },
      );
      return res.data.data.rows;
    },
  });
}

export function useUpsertParameter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      key: string;
      value: string;
      valueType?: PricingParameterValueType;
      description?: string;
    }) => {
      const { key, ...body } = input;
      const res = await apiClient.put<ApiSuccess<PricingParameter>>(
        `${BASE}/parameters/${encodeURIComponent(key)}`,
        body,
      );
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: pricingTablesKeys.all }),
  });
}
